/**
 * The Markdown subset contributors write in, sanitised and parsed.
 *
 * Task descriptions, acceptance criteria and rejection reasons are the first
 * place in this system where one person's text is rendered in another person's
 * browser. That is the classic injection surface, so this module is built from
 * two independent guarantees rather than one:
 *
 *   1. {@link sanitizeMarkdown} runs on the **server**, before storage. It
 *      normalises line endings, removes control characters, strips anything
 *      shaped like an HTML tag, neutralises dangerous URL schemes, and bounds
 *      the length. What reaches the database is already safe as source text.
 *   2. {@link parseMarkdown} produces a **structured document** — never an HTML
 *      string. The web client renders that structure as React elements, so
 *      text becomes text nodes and there is no `innerHTML` anywhere in the
 *      product. Even if layer 1 were bypassed entirely, there is no code path
 *      that turns contributor text into markup.
 *
 * Both layers live here, in the pure package, because the handoff requires the
 * client's preview to render through *the same* sanitiser as the record: a
 * preview that is more permissive than storage promises the author something
 * the system will not keep, and a preview that is stricter makes them think
 * their input was rejected when it was not.
 *
 * The grammar is deliberately small — headings, paragraphs, lists,
 * checklists, tables, block quotes, fenced code, thematic breaks, and inline
 * emphasis, code and links. Checklists and pipe tables joined for the Data
 * Room, whose documents are exactly the kind of writing that needs them;
 * both parse to structure like everything else, so they add no escaping
 * surface. Images, footnotes, raw HTML and reference links remain absent by
 * decision, not by omission: each one is a rendering surface with its own
 * escaping rules, and none of them is needed here.
 */
/**
 * The default storage bound. Matches the CHECK in migration 0009 for task
 * text; the Data Room passes its own larger bound (migration 0012) through
 * the `maxLength` parameter below.
 */
export const MAX_MARKDOWN_LENGTH = 20_000;
/**
 * Anything that looks like an HTML tag or comment.
 *
 * Matched loosely on purpose. This is not trying to parse HTML — it is
 * removing every construct a browser might one day treat as markup, including
 * the malformed ones parsers are famously lenient about.
 */
const TAG_LIKE = /<!--[\s\S]*?-->|<\/?[A-Za-z!/?][^\n>]*>/g;
/** Control characters that never carry meaning in a task description. */
const CONTROL_CHARACTERS = new RegExp('[' + String.raw `\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F` + ']', 'g');
/**
 * Bidirectional override characters.
 *
 * Removed because they can make rendered text read in an order that does not
 * match the characters stored — the "Trojan Source" problem. Arabic content is
 * unaffected: ordinary right-to-left text needs none of these, and the
 * document's own `dir` attribute does that work.
 */
const BIDI_OVERRIDES = new RegExp('[' + String.raw `\u202A-\u202E\u2066-\u2069` + ']', 'g');
/**
 * Schemes a link may use.
 *
 * `javascript:` and `data:` are the two that turn a link into code, and both
 * survive a surprising amount of mangling, so the check is an allowlist rather
 * than a denylist.
 */
function isSafeHref(href) {
    const trimmed = href.trim();
    if (trimmed.length === 0 || trimmed.length > 2000)
        return false;
    // Relative in-app links are permitted; they cannot carry a scheme at all.
    if (trimmed.startsWith('/') && !trimmed.startsWith('//'))
        return true;
    return /^https?:\/\/[^\s<>"']+$/i.test(trimmed);
}
/**
 * Make contributor text safe to store.
 *
 * Idempotent: sanitising already-sanitised text returns the same text with
 * both flags false, which is what lets the client preview call this on every
 * keystroke without the content drifting.
 *
 * `maxLength` defaults to the task bound; a surface with a different storage
 * CHECK passes its own, and the preview must pass the same one it stores
 * under or the record and the preview drift apart.
 */
export function sanitizeMarkdown(input, maxLength = MAX_MARKDOWN_LENGTH) {
    const before = input;
    let text = input
        .replaceAll('\r\n', '\n')
        .replaceAll('\r', '\n')
        .replace(CONTROL_CHARACTERS, '')
        .replace(BIDI_OVERRIDES, '')
        .replace(TAG_LIKE, '')
        // Whatever `<` survives was not part of a complete tag — an unterminated
        // `<img src=x onerror=…` with no closing bracket, or a stray comparison.
        // Only the bracket is removed, not the rest of the line: a sanitiser that
        // silently deletes the remainder of somebody's paragraph because they
        // typed one angle bracket is a data-loss bug wearing a security costume.
        // The surviving text is text, and the parser has no node that can carry
        // markup, so there is nothing left for it to become.
        .replaceAll('<', '');
    // Collapse runs of blank lines: three or more carry no additional meaning
    // and are the usual way a paste turns into a page of whitespace.
    text = text.replace(/\n{3,}/g, '\n\n');
    // Trailing spaces are invisible and change nothing this grammar renders.
    text = text
        .split('\n')
        .map((line) => line.replace(/[ \t]+$/, ''))
        .join('\n')
        .trim();
    const stripped = text !== before.replace(/\r\n/g, '\n').trim();
    let truncated = false;
    if (text.length > maxLength) {
        text = text.slice(0, maxLength).trimEnd();
        truncated = true;
    }
    return { text, truncated, stripped };
}
// --------------------------------------------------------------------------- //
// Inline parsing
// --------------------------------------------------------------------------- //
/**
 * Parse one line's inline constructs.
 *
 * A hand-written scanner rather than a chain of regular expressions with
 * replacement: replacement chains produce strings, strings invite `innerHTML`,
 * and the whole point of this module is that contributor text never becomes
 * markup. The scanner emits nodes.
 *
 * Code spans win over everything else, so `` `**not bold**` `` is literal —
 * the ordinary expectation, and the one that keeps a snippet of syntax
 * readable in a task description.
 */
export function parseInline(source) {
    const nodes = [];
    let plain = '';
    const flush = () => {
        if (plain.length > 0) {
            nodes.push({ kind: 'text', text: plain });
            plain = '';
        }
    };
    let index = 0;
    while (index < source.length) {
        const rest = source.slice(index);
        // Escaped punctuation: a backslash makes the next character literal.
        if (rest.startsWith('\\') && rest.length > 1) {
            plain += rest[1] ?? '';
            index += 2;
            continue;
        }
        // Code span.
        if (rest.startsWith('`')) {
            const end = rest.indexOf('`', 1);
            if (end > 1) {
                flush();
                nodes.push({ kind: 'code', text: rest.slice(1, end) });
                index += end + 1;
                continue;
            }
        }
        // Link: [text](href). The href is validated, and an unsafe one degrades
        // to the label as plain text rather than to a dead or dangerous anchor.
        if (rest.startsWith('[')) {
            const close = rest.indexOf(']');
            if (close > 0 && rest[close + 1] === '(') {
                const hrefEnd = rest.indexOf(')', close + 2);
                if (hrefEnd > close + 1) {
                    const label = rest.slice(1, close);
                    const href = rest.slice(close + 2, hrefEnd);
                    flush();
                    if (isSafeHref(href)) {
                        nodes.push({
                            kind: 'link',
                            href: href.trim(),
                            children: parseInline(label),
                        });
                    }
                    else {
                        nodes.push(...parseInline(label));
                    }
                    index += hrefEnd + 1;
                    continue;
                }
            }
        }
        // Strong, then emphasis. Longest delimiter first, or `**x**` parses as an
        // emphasised `*x*`.
        const strong = matchDelimited(rest, '**');
        if (strong !== null) {
            flush();
            nodes.push({ kind: 'strong', children: parseInline(strong.inner) });
            index += strong.length;
            continue;
        }
        const emphasis = matchDelimited(rest, '*') ?? matchDelimited(rest, '_');
        if (emphasis !== null) {
            flush();
            nodes.push({ kind: 'emphasis', children: parseInline(emphasis.inner) });
            index += emphasis.length;
            continue;
        }
        plain += source[index] ?? '';
        index += 1;
    }
    flush();
    return nodes;
}
/** A balanced `delimiter … delimiter` run with non-empty contents. */
function matchDelimited(source, delimiter) {
    if (!source.startsWith(delimiter))
        return null;
    const end = source.indexOf(delimiter, delimiter.length);
    if (end <= delimiter.length)
        return null;
    return {
        inner: source.slice(delimiter.length, end),
        length: end + delimiter.length,
    };
}
// --------------------------------------------------------------------------- //
// Block parsing
// --------------------------------------------------------------------------- //
const HEADING = /^(#{1,3})\s+(.*)$/;
const UNORDERED_ITEM = /^[-*]\s+(.*)$/;
const ORDERED_ITEM = /^\d{1,3}[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^(?:-{3,}|\*{3,}|_{3,})$/;
const FENCE = /^```/;
const CHECKBOX = /^\[([ xX])\]\s+(.*)$/;
/** The `| --- | :--: |` line that makes the row above it a table header. */
const TABLE_SEPARATOR = /^\|?(?:\s*:?-{3,}:?\s*\|)*\s*:?-{3,}:?\s*\|?$/;
/** Split a table row into cells. A backslash keeps a pipe literal. */
function tableCells(line) {
    const cells = [];
    let cell = '';
    let index = 0;
    while (index < line.length) {
        const character = line[index] ?? '';
        if (character === '\\' && line[index + 1] === '|') {
            cell += '\\|';
            index += 2;
            continue;
        }
        if (character === '|') {
            cells.push(cell);
            cell = '';
            index += 1;
            continue;
        }
        cell += character;
        index += 1;
    }
    cells.push(cell);
    // A leading or trailing pipe produces an empty edge cell that is
    // punctuation, not content.
    if (cells.length > 0 && (cells[0] ?? '').trim() === '')
        cells.shift();
    if (cells.length > 0 && (cells[cells.length - 1] ?? '').trim() === '')
        cells.pop();
    return cells.map((text) => text.trim());
}
/** Does a table start here — a piped row with a separator directly under it? */
function isTableStart(lines, index) {
    const line = lines[index] ?? '';
    const next = lines[index + 1] ?? '';
    return (line.includes('|') &&
        tableCells(line).length > 0 &&
        TABLE_SEPARATOR.test(next.trim()) &&
        next.includes('-'));
}
/**
 * Parse sanitised Markdown into blocks.
 *
 * Takes text that has already been through {@link sanitizeMarkdown}; calling it
 * on raw input would still be safe — the output is structure, not markup — but
 * the stored record and the preview would then be parsing different strings,
 * which is the drift this module exists to prevent.
 */
export function parseMarkdown(source) {
    const lines = source.split('\n');
    const blocks = [];
    let index = 0;
    while (index < lines.length) {
        const line = lines[index] ?? '';
        if (line.trim().length === 0) {
            index += 1;
            continue;
        }
        // Fenced code. Everything until the closing fence is literal, including
        // lines that would otherwise be headings or list items.
        if (FENCE.test(line)) {
            const collected = [];
            index += 1;
            while (index < lines.length && !FENCE.test(lines[index] ?? '')) {
                collected.push(lines[index] ?? '');
                index += 1;
            }
            // An unterminated fence ends at the end of the document rather than
            // discarding the content the author typed.
            index += 1;
            blocks.push({ kind: 'code', text: collected.join('\n') });
            continue;
        }
        if (RULE.test(line.trim())) {
            blocks.push({ kind: 'rule' });
            index += 1;
            continue;
        }
        // Pipe table: a header row, the separator that promotes it, then data
        // rows until the pipes stop. Ragged rows are squared to the header's
        // width — a missing cell renders empty, a surplus one is dropped —
        // because a table that shifts columns mid-body misreads worse than
        // either repair.
        if (isTableStart(lines, index)) {
            const header = tableCells(lines[index] ?? '').map(parseInline);
            index += 2;
            const rows = [];
            while (index < lines.length && (lines[index] ?? '').includes('|')) {
                const cells = tableCells(lines[index] ?? '');
                if (cells.length === 0)
                    break;
                rows.push(header.map((_, column) => parseInline(cells[column] ?? '')));
                index += 1;
            }
            blocks.push({ kind: 'table', header, rows });
            continue;
        }
        const heading = HEADING.exec(line);
        if (heading !== null) {
            const hashes = heading[1] ?? '#';
            blocks.push({
                kind: 'heading',
                level: hashes.length,
                children: parseInline(heading[2] ?? ''),
            });
            index += 1;
            continue;
        }
        if (UNORDERED_ITEM.test(line) || ORDERED_ITEM.test(line)) {
            const ordered = ORDERED_ITEM.test(line);
            const items = [];
            while (index < lines.length) {
                const current = lines[index] ?? '';
                const match = ordered
                    ? ORDERED_ITEM.exec(current)
                    : UNORDERED_ITEM.exec(current);
                if (match === null)
                    break;
                const text = match[1] ?? '';
                // A checklist item is an ordinary item whose text opens with a
                // checkbox. The box is state, not content, so it leaves the text.
                const checkbox = CHECKBOX.exec(text);
                if (checkbox !== null) {
                    items.push({
                        children: parseInline(checkbox[2] ?? ''),
                        checked: (checkbox[1] ?? ' ') !== ' ',
                    });
                }
                else {
                    items.push({ children: parseInline(text), checked: null });
                }
                index += 1;
            }
            blocks.push({ kind: 'list', ordered, items });
            continue;
        }
        if (QUOTE.test(line)) {
            const collected = [];
            while (index < lines.length) {
                const match = QUOTE.exec(lines[index] ?? '');
                if (match === null)
                    break;
                collected.push(match[1] ?? '');
                index += 1;
            }
            blocks.push({ kind: 'quote', children: parseInline(collected.join(' ')) });
            continue;
        }
        // A paragraph runs until a blank line or the start of another construct.
        const collected = [];
        while (index < lines.length) {
            const current = lines[index] ?? '';
            if (current.trim().length === 0 ||
                FENCE.test(current) ||
                RULE.test(current.trim()) ||
                HEADING.test(current) ||
                UNORDERED_ITEM.test(current) ||
                ORDERED_ITEM.test(current) ||
                QUOTE.test(current) ||
                isTableStart(lines, index)) {
                break;
            }
            collected.push(current);
            index += 1;
        }
        blocks.push({ kind: 'paragraph', children: parseInline(collected.join(' ')) });
    }
    return blocks;
}
/**
 * The plain text of a document, for summaries and accessible descriptions.
 *
 * Not a rendering path — it exists so a card can show the first line of a
 * description without the caller reaching for a regular expression over
 * Markdown source and getting the escaping subtly wrong.
 */
export function markdownToPlainText(source) {
    const flatten = (nodes) => nodes
        .map((node) => {
        switch (node.kind) {
            case 'text':
                return node.text;
            case 'code':
                return node.text;
            default:
                return flatten(node.children);
        }
    })
        .join('');
    return parseMarkdown(source)
        .map((block) => {
        switch (block.kind) {
            case 'code':
                return block.text;
            case 'rule':
                return '';
            case 'list':
                return block.items.map((item) => flatten(item.children)).join(' ');
            case 'table':
                return [...block.header, ...block.rows.flat()].map(flatten).join(' ');
            default:
                return flatten(block.children);
        }
    })
        .filter((part) => part.length > 0)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}
//# sourceMappingURL=markdown.js.map